import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plane, Plus, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export const DEFAULT_TRIP_IMG = "https://images.unsplash.com/photo-1483683804023-6ccdb62f86ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx0cm9waWNhbCUyMHRyYXZlbCUyMHBhcmFkaXNlJTIwYWVyaWFsfGVufDB8fHx8MTc4NTg4OTY5Mnww&ixlib=rb-4.1.0&q=85";

export const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000);
};

const fmtDate = (d, withYear = false) => {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
};

const STATUS_STYLE = {
  dreaming: "text-zinc-400 border-white/15 bg-white/5",
  planned: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
  booked: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
};

const EMPTY_FORM = { title: "", destination: "", start_date: "", end_date: "", status: "dreaming" };

export const TripForm = ({ onSaved, compact = false }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/trips", form);
      toast.success("Locked in — something to look forward to");
      setForm(EMPTY_FORM);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className={`grid gap-3 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="trip-form">
      <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Destination — e.g. Bali" className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]" data-testid="trip-destination-input" />
      <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Trip — e.g. Family surf week" className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]" data-testid="trip-title-input" />
      <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} style={{ colorScheme: "dark" }} className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]" data-testid="trip-start-input" />
      <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} style={{ colorScheme: "dark" }} className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]" data-testid="trip-end-input" />
      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
        <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm" data-testid="trip-status-select">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent className="bg-[#17171A] border-white/10 text-white">
          {["dreaming", "planned", "booked"].map((s) => (
            <SelectItem key={s} value={s} data-testid={`trip-status-${s}`}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 px-5 py-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-[#e5c555] transition-colors duration-200 disabled:opacity-50" data-testid="trip-save-button">
        <Plus className="w-4 h-4" /> {saving ? "Saving…" : "Add Trip"}
      </button>
    </form>
  );
};

export const TravelWidget = () => {
  const [trips, setTrips] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/trips");
    setTrips(data.trips);
  }, []);
  useEffect(() => { load().catch((err) => console.error("Travel widget load failed:", err)); }, [load]);

  const next = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return trips.filter((t) => t.start_date && t.start_date >= today)[0] || null;
  }, [trips]);

  const days = next ? daysUntil(next.start_date) : null;

  return (
    <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="travel-widget">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-md overflow-hidden border border-white/10 min-h-72">
        <img src={next?.image_url || DEFAULT_TRIP_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/10" />
        <div className="relative p-8 flex flex-col h-full justify-between gap-8">
          <div className="flex items-center justify-between">
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
              <Plane className="w-3.5 h-3.5" /> Travel & Experiences
            </p>
            <Link to="/travel" className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200 inline-flex items-center gap-1" data-testid="travel-view-all">
              All trips <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {next ? (
            <div data-testid="travel-next-trip">
              <h3 className="font-display text-3xl sm:text-4xl text-white">{next.destination}</h3>
              <p className="text-sm text-zinc-400 mt-1">{next.title}</p>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                {next.start_date && (
                  <span className="text-xs text-zinc-300 border border-white/10 bg-black/40 px-3 py-1.5 rounded-sm" data-testid="travel-dates">
                    {fmtDate(next.start_date)}{next.end_date ? ` → ${fmtDate(next.end_date, true)}` : ""}
                  </span>
                )}
                {days !== null && days >= 0 && (
                  <span className="text-xs font-bold uppercase tracking-widest text-black bg-[#D4AF37] px-3 py-1.5 rounded-sm" data-testid="travel-countdown">
                    {days === 0 ? "Today" : `${days} days away`}
                  </span>
                )}
                <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border ${STATUS_STYLE[next.status]}`} data-testid="travel-status">{next.status}</span>
              </div>
            </div>
          ) : (
            <div data-testid="travel-empty">
              <h3 className="font-display text-3xl sm:text-4xl text-white">Nothing on the horizon.</h3>
              <p className="text-sm text-zinc-400 mt-1">The whole point of the empire. Plan the next one.</p>
            </div>
          )}
          <div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200"
              data-testid="travel-plan-toggle"
            >
              <Plus className="w-3.5 h-3.5" /> {showForm ? "Close" : "Plan the next one"}
            </button>
            {showForm && (
              <div className="mt-4 backdrop-blur-2xl bg-black/60 border border-white/10 rounded-sm p-4">
                <TripForm compact onSaved={() => { setShowForm(false); load(); }} />
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
};
